import {calculateAge, getDateFromIso8601String} from '../components/DatePicker';
import { mapCountry } from '../components/CountrySelect';
import { isCanadianClient, isUKOrder, isCountryUk, getValidFormattedUKPostalCode } from '../util/gearOrderUtil';
import { WAIVED_SHIPPING_METHOD_AFFIX } from '../components/shipping-method/WaiveShippingFeeCheckbox';

const shippingMethods = {
  GROUND: 'FedEx Ground',
  SECOND_DAY: 'FedEx Second Day',
  OVERNIGHT: 'FedEx Overnight',
  SMART_POST: 'FedEx Smart Post'
};

const validStates = '^([Aa][Ll]|[Aa][Kk]|[Aa][Zz]|[Aa][Rr]|[Cc][Aa]|[Cc][Oo]|[Cc][Tt]|[Dd][Cc]|[Dd][Ee]|[Ff][Ll]|[Gg][Aa]|[Hh][Ii]|[Ii][Dd]|[Ii][Ll]|[Ii][Nn]|[Ii][Aa]|[Kk][Ss]|[Kk][Yy]|[Ll][Aa]|[Mm][Ee]|[Mm][Dd]|[Mm][Aa]|[Mm][Ii]|[Mm][Nn]|[Mm][Ss]|[Mm][Oo]|[Mm][Tt]|[Nn][Ee]|[Nn][Vv]|[Nn][Hh]|[Nn][Jj]|[Nn][Mm]|[Nn][Yy]|[Nn][Cc]|[Nn][Dd]|[Oo][Hh]|[Oo][Kk]|[Oo][Rr]|[Pp][Aa]|[Rr][Ii]|[Ss][Cc]|[Ss][Dd]|[Tt][Nn]|[Tt][Xx]|[Uu][Tt]|[Vv][Tt]|[Vv][Aa]|[Ww][Aa]|[Ww][Vv]|[Ww][Ii]|[Ww][Yy]|[Aa][Bb]|[Bb][Cc]|[Mm][Bb]|[Nn][Bb]|[Nn][Ll]|[Nn][Tt]|[Nn][Ss]|[Nn][Uu]|[Oo][Nn]|[Pp][Ee]|[Qq][Cc]|[Ss][Kk]|[Yy][Tt]|[Aa][Ss]|[Ff][Mm]|[Gg][Uu]|[Mm][Hh]|[Mm][Pp]|[Pp][Ww]|[Pp][Rr]|[Vv][Ii]|[Aa][Aa]|[Aa][Ee]|[Aa][Pp]|)$';
const validPitlStates = [
  'AL', 'AK', 'AZ', 'AR', 'CA', 'CO', 'CT', 'DC', 'DE', 'FL',
  'GA', 'HI', 'ID', 'IL', 'IN', 'IA', 'KS', 'KY', 'LA', 'ME', 'MD', 'MA', 'MI',
  'MN', 'MS', 'MO', 'MT', 'NE', 'NV', 'NH', 'NM', 'NC', 'ND', 'OH', 'OK', 'OR',
  'PA', 'SC', 'SD', 'TN', 'TX', 'UT', 'VT', 'VA', 'WA', 'WV', 'WI', 'WY'];
const employerStates = ['NH', 'OH'];

/**
 * Builds the form object used by the PatientInfo page from the given gear order.
 *
 * @param gearOrder the gear order from which to build the form
 * @return the form to be used for validation and display
 */
const buildPatientInfoFormFromOrder = (gearOrder) => {
  let form = {};
  let employmentStatus = 'Employed';
  if (gearOrder) {
    let shippingAddress = gearOrder.patientInfo
    && gearOrder.patientInfo.addresses
        ? gearOrder.patientInfo.addresses.find(
            address => address.type === 'shipping')
        : null;
    let employerAddress = gearOrder.patientInfo
    && gearOrder.patientInfo.addresses
        ? gearOrder.patientInfo.addresses.find(
            address => address.type === 'employer')
        : null;

    let country = shippingAddress?.country || (gearOrder.containsDropShipRequisition ? gearOrder.practitioner.office.address.country : null);
    if(!country && isUKOrder(gearOrder)) {
      country = 'United Kingdom';
    }
    let defaultShippingMethod = isUKOrder(gearOrder) ? (isCountryUk(country) ? `Royal Mail Tracked (2-3 day delivery)` : `Royal Mail Special Delivery (next working day)`)
                                : (gearOrder.containsDropShipRequisition || gearOrder.physicianInTheLoop) ? 'FedEx Ground' : null;

    if (gearOrder.patientInfo && gearOrder.patientInfo.occupation) {
      if (gearOrder.patientInfo.occupation === 'Unemployed'
          || gearOrder.patientInfo.occupation === 'Student'
          || gearOrder.patientInfo.occupation === 'Retired') {
        employmentStatus = gearOrder.patientInfo.occupation;
      }
    }
    form.nameFirst = {
      value: gearOrder.patientInfo ? gearOrder.patientInfo.nameFirst : null
    };
    form.nameLast = {
      value: gearOrder.patientInfo ? gearOrder.patientInfo.nameLast : null
    };
    form.nameMiddle = {
      value: gearOrder.patientInfo ? gearOrder.patientInfo.nameMiddle : null
    };
    form.dateOfBirth = {
      value: gearOrder.patientInfo ? gearOrder.patientInfo.dateOfBirth : ''
    };
    form.age = {
      value: gearOrder.patientInfo ? calculateAge(
          getDateFromIso8601String(gearOrder.patientInfo.dateOfBirth)) : ''
    };
    form.gender = {
      value: gearOrder.patientInfo ? gearOrder.patientInfo.gender : null
    };
    form.driverLicenseNumber = {
      value: gearOrder.patientInfo ? gearOrder.patientInfo.driverLicenseNumber
          : null
    };
    form.ssn = {
      value: gearOrder.patientInfo ? gearOrder.patientInfo.ssn : null
    };
    form.race = {
      value: gearOrder.patientInfo ? gearOrder.patientInfo.race : null
    };
    form.ethnicity = {
      value: gearOrder.patientInfo ? gearOrder.patientInfo.ethnicity : null
    };
    form.responsibleNameFirst = {
      value: gearOrder.patientInfo ? gearOrder.patientInfo.responsibleNameFirst
          : null
    };
    form.responsibleNameMiddle = {
      value: gearOrder.patientInfo ? gearOrder.patientInfo.responsibleNameMiddle
          : null
    };
    form.responsibleNameLast = {
      value: gearOrder.patientInfo ? gearOrder.patientInfo.responsibleNameLast
          : null
    };
    form.occupation = {
      value: gearOrder.patientInfo ? gearOrder.patientInfo.occupation : null
    };
    form.employer = {
      value: gearOrder.patientInfo ? gearOrder.patientInfo.employer : null
    };
    form.email = {
      value: gearOrder.patientInfo ? gearOrder.patientInfo.email : null
    };
    form.chartNumber = {
      value: gearOrder.patientInfo ? gearOrder.patientInfo.chartNumber : null
    };
    form.source = {
      value: gearOrder.patientInfo ? gearOrder.patientInfo.source : null
    };
    form.address1 = {value: shippingAddress ? shippingAddress.address1 : null};
    form.address2 = {value: shippingAddress ? shippingAddress.address2 : null};
    form.city = {value: shippingAddress ? shippingAddress.city : null};
    form.state = {value: shippingAddress ? shippingAddress.state : null};
    form.zipCode = {value: shippingAddress ? shippingAddress.zipCode : null};
    form.country = {
      value: !gearOrder.physicianInTheLoop ? mapCountry(
          shippingAddress?.country) : 'United States'
    };
    form.county = {value: shippingAddress ? shippingAddress.county : null};
    
    const phoneCountry = shippingAddress?.country ? shippingAddress.country : (gearOrder.practitioner?.office?.address?.country || 'United States');
    form.voicePhone = {
      value: formatInitialPhoneNumber(shippingAddress?.voicePhone, phoneCountry)
    };
    form.alternatePhone = {
      value: formatInitialPhoneNumber(shippingAddress?.alternatePhone, phoneCountry)
    };
    form.employerAddress1 = {
      value: employerAddress ? employerAddress.address1 : null
    };
    form.employerAddress2 = {
      value: employerAddress ? employerAddress.address2 : null
    };
    form.employerCity = {value: employerAddress ? employerAddress.city : null};
    form.employerState = {
      value: employerAddress ? employerAddress.state : null
    };
    form.employerZipCode = {
      value: employerAddress ? employerAddress.zipCode : null
    };
    form.employerVoicePhone = {
      value: formatInitialPhoneNumber(employerAddress?.voicePhone)
    };
    form.employerSchoolDistrict = {
      value: employerAddress ? employerAddress.schoolDistrict : null
    };
    form.employmentStatus = {value: employmentStatus ? employmentStatus : null};
    form.shippingMethod = {
      value: mapOrderShippingMethod(
          gearOrder.shippingMethod || defaultShippingMethod)
    };

    if(isUKOrder(gearOrder)) {
      form.inboundShippingMethod = {value: gearOrder.inboundShippingMethod};
    }

    if (!shippingAddress?.country && gearOrder.containsDropShipRequisition) {
      form.country.value = gearOrder.practitioner.office.address.country;
    }
  }

  return form;
};

/**
 * Map old shipping method text to new text.
 *
 * The shipping method text has changed but we still need to map old values to new values.
 * Check for specific words in the order's shipping method and return the new text for that method.
 * Example: Old 'FedEx Ground - $14.99' -> New 'FedEx Ground'
 * @param shippingMethod The original shipping method text to be updated
 * @return The new shipping method text
 */
const mapOrderShippingMethod = (shippingMethod) => {
  if (shippingMethod && !shippingMethod.includes(WAIVED_SHIPPING_METHOD_AFFIX)) {
    if (shippingMethod.indexOf('Ground') > -1) {
      shippingMethod = shippingMethods.GROUND;
    } else if (shippingMethod.indexOf('Second') > -1) {
      shippingMethod = shippingMethods.SECOND_DAY;
    } else if (shippingMethod.indexOf('Overnight') > -1) {
      shippingMethod = shippingMethods.OVERNIGHT;
    } else if (shippingMethod.indexOf('Smart') > -1) {
      shippingMethod = shippingMethods.SMART_POST;
    }
  }

  return shippingMethod;
};

/**
 * Checks if employer information needs to be validated based on the address' state.
 *
 * @param addrState the address object
 * @return true if employer validation is required, false if not
 */
const isEmployerValidationRequired = (addrState) => {
  return addrState && employerStates.includes(addrState.toUpperCase());
};

/**
 * Returns whether the patient is a minor (less than 18 years old)
 *
 * @param dateOfBirth the patient's date of birth
 * @return true if the patient is a minor, false otherwise
 */
const isPatientMinor = (dateOfBirth) => {
  let isMinor = false;
  if (dateOfBirth) {
    let dob = new Date(dateOfBirth);
    isMinor = calculateAge(dob) < 18;
  }
  return isMinor;
};

/**
 * Update the patient information form with validation details.
 *
 * @param form the initial patient information form
 * @param gearOrder the current gear order
 * @return the patient information form, updated with validation details
 */
const validatePatientInfoForm = (form, gearOrder, forceFullRegistration) => {
  let validatedForm = Object.assign({}, form);
  Object.values(validatedForm).forEach((prop) => {
    prop.valid = true;
    prop.message = '';
  });

  const employerRequired =
      isEmployerValidationRequired(form.state?.value) ||
      isEmployerValidationRequired(form.employerState?.value);

  const isMinor = isPatientMinor(form?.dateOfBirth?.value);
  const fullRegistrationRequired = gearOrder.containsDropShipRequisition
      || gearOrder.containsCovidRegistration || forceFullRegistration || isUKOrder(gearOrder);

  const reqLayoutId = gearOrder.practitioner?.office?.reqLayoutId ?? 0;
  const isInternational = (reqLayoutId !== 0 && reqLayoutId !== 2)
      || (validatedForm.country.value && validatedForm.country.value
          !== 'United States');
  const isCanadaClient = isCanadianClient(gearOrder);

  if (!validatedForm.nameFirst.value) {
    validatedForm.nameFirst.valid = false;
    validatedForm.nameFirst.message = 'Please provide a first name.';
  }
  if (!validatedForm.nameLast.value) {
    validatedForm.nameLast.valid = false;
    validatedForm.nameLast.message = 'Please provide a last name.';
  }
  if (!validatedForm.dateOfBirth.value) {
    validatedForm.dateOfBirth.valid = false;
    validatedForm.dateOfBirth.message = 'Please provide a date of birth.';
  } else {
    try {
      const dobDate = getDateFromIso8601String(validatedForm.dateOfBirth.value);
      const age = calculateAge(dobDate);
      if (age < 1 && !validatedForm.dateOfBirth.confirmedAge) {
        validatedForm.dateOfBirth.valid = false;
        validatedForm.dateOfBirth.confirmedAge = false;
        validatedForm.dateOfBirth.message = 'Please confirm patient\'s age.';
      }
    } catch (err) {
      validatedForm.dateOfBirth.valid = false;
      validatedForm.dateOfBirth.message = `${validatedForm.dateOfBirth.value} is not a valid date format. Please use YYYY-MM-DD or MM/DD/YYYY.`;
    }
  }
  if (!validatedForm.gender.value) {
    validatedForm.gender.valid = false;
    validatedForm.gender.message = 'Please provide a sex.';
  }
  if (!validatedForm.email.value && (gearOrder.physicianInTheLoop || forceFullRegistration)) {
    validatedForm.email.valid = false;
    validatedForm.email.message = 'Please provide an email address.';
  }

  if ((!gearOrder.containsDropShipRequisition || gearOrder.dataConfirmed || gearOrder.physicianInTheLoop) && isMinor) {
	if (!validatedForm.responsibleNameFirst.value) {
	  validatedForm.responsibleNameFirst.valid = false;
	  validatedForm.responsibleNameFirst.message = 'Please provide a responsible party first name.';
	}
	if (!validatedForm.responsibleNameLast.value) {
	  validatedForm.responsibleNameLast.valid = false;
	  validatedForm.responsibleNameLast.message = 'Please provide a responsible party last name.';
	}
  }

  if (fullRegistrationRequired && (!isInternational || isCanadaClient || isUKOrder(gearOrder))) {
    if (validatedForm.email.value) {
		if(!validateEmailFormat(validatedForm.email.value)) {
        validatedForm.email.valid = false;
        validatedForm.email.message = 'Please provide a valid email address.';
      }
    }
    if ((!isInternational || isCanadaClient || isUKOrder(gearOrder)) && !validatedForm.voicePhone.value) {
      validatedForm.voicePhone.valid = false;
      validatedForm.voicePhone.message = 'Please provide a phone number.';
    } else if (validatedForm.voicePhone.value?.replace(/\D/g, '').length < 11) {
      validatedForm.voicePhone.valid = false;
      validatedForm.voicePhone.message = 'Please provide a valid phone number.';
    }
    if (!validatedForm.address1.value) {
      validatedForm.address1.valid = false;
      validatedForm.address1.message = 'Please provide a street address.';
    }
    if (!validatedForm.city.value) {
      validatedForm.city.valid = false;
      validatedForm.city.message = 'Please provide a city.';
    }
    if (!isUKOrder(gearOrder) && !validatedForm.state.value) {
      validatedForm.state.valid = false;
      validatedForm.state.message = 'Please provide a state.';
    } else if (gearOrder.physicianInTheLoop && !validPitlStates.includes(
        validatedForm.state.value.toUpperCase())) {
      validatedForm.state.valid = false;
      validatedForm.state.message = 'Please provide a valid state.';
    } else if (!isInternational && !gearOrder.physicianInTheLoop && !new RegExp(validStates).test(
        validatedForm.state.value)) {
      validatedForm.state.valid = false;
      validatedForm.state.message = 'Please provide a valid state.';
    }

    if (!validatedForm.zipCode.value) {
      validatedForm.zipCode.valid = false;
      validatedForm.zipCode.message = `Please provide a ${isInternational
          ? 'postal' : 'zip'} code.`;
    } else if (isUKOrder(gearOrder) && isCountryUk(validatedForm?.country?.value) && getValidFormattedUKPostalCode(validatedForm.zipCode.value) == null) {
      validatedForm.zipCode.valid = false;
      validatedForm.zipCode.message = `Please provide a valid postal code`;
    }

    if (!validatedForm.country.value) {
      validatedForm.country.valid = false;
      validatedForm.country.message = 'Please provide a country.';
    }
    let isEmployed = validatedForm.occupation.value === 'Student'
        || validatedForm.employmentStatus.value === 'Employed';
    if (!gearOrder.containsDropShipRequisition && employerRequired
        && isEmployed) {
      let employerStr = 'an employer';
      if (!validatedForm.occupation.value) {
        validatedForm.occupation.valid = false;
        validatedForm.occupation.message = 'Please provide an occupation.';
      } else if (validatedForm.occupation.value === 'Student') {
        employerStr = 'a school';
        if (validatedForm.state?.value?.toUpperCase() === 'NY'
            && !validatedForm.employerSchoolDistrict.value) {
          validatedForm.employerSchoolDistrict.valid = false;
          validatedForm.employerSchoolDistrict.message = 'Please provide a school district.';
        }
      }
      if (!validatedForm.employer.value) {
        validatedForm.employer.valid = false;
        validatedForm.employer.message = 'Please provide ' + employerStr
            + ' name.';
      }
      if (!validatedForm.employerAddress1.value) {
        validatedForm.employerAddress1.valid = false;
        validatedForm.employerAddress1.message = 'Please provide ' + employerStr
            + ' street address.';
      }
      if (!validatedForm.employerCity.value) {
        validatedForm.employerCity.valid = false;
        validatedForm.employerCity.message = 'Please provide ' + employerStr
            + ' city.';
      }
      if (!validatedForm.employerState.value) {
        validatedForm.employerState.valid = false;
        validatedForm.employerState.message = 'Please provide ' + employerStr
            + ' state.';
      } else if (!new RegExp(validStates).test(
          validatedForm.employerState.value)) {
        validatedForm.employerState.valid = false;
        validatedForm.employerState.message = 'Please provide a valid state.';
      }
      if (!validatedForm.employerZipCode.value) {
        validatedForm.employerZipCode.valid = false;
        validatedForm.employerZipCode.message = 'Please provide ' + employerStr
            + ' zip code.';
      }
      if (!validatedForm.employerVoicePhone.value) {
        validatedForm.employerVoicePhone.valid = false;
        validatedForm.employerVoicePhone.message = 'Please provide a phone number.';
      } else if (validatedForm.employerVoicePhone.value.replace(/\D/g,
          '').length < 11) {
        validatedForm.employerVoicePhone.valid = false;
        validatedForm.employerVoicePhone.message = 'Please provide a valid phone number.';
      }
    } else if (employerRequired && isEmployed) {
      if (validatedForm.employerState.value && !new RegExp(validStates).test(
          validatedForm.employerState.value)) {
        validatedForm.employerState.valid = false;
        validatedForm.employerState.message = 'Please provide a valid state.';
      }
      if (validatedForm.employerVoicePhone.value
          && validatedForm.employerVoicePhone.value.replace(/\D/g, '').length
          < 11) {
        validatedForm.employerVoicePhone.valid = false;
        validatedForm.employerVoicePhone.message = 'Please provide a valid phone number.';
      }
    }
  }

  // SCR-5897: Require race, ethnicity, county for Covid orders
  if (gearOrder.containsCovidRegistration && !isInternational) {
    if (!validatedForm.race.value) {
      validatedForm.race.valid = false;
      validatedForm.race.message = 'Race is required for Covid-19 State Reporting.';
    }
    if (!validatedForm.ethnicity.value) {
      validatedForm.ethnicity.valid = false;
      validatedForm.ethnicity.message = 'Ethnicity is required for Covid-19 State Reporting.';
    }
    if (!validatedForm.county.value) {
      validatedForm.county.valid = false;
      validatedForm.county.message = 'Please provide a county.';
    }

    let billingMethod = gearOrder.requisitions?.[0]?.billingMethod;
    if (
        billingMethod?.indexOf('HRSA') > -1 &&
        !validatedForm.ssn.value &&
        !validatedForm.driverLicenseNumber.value
    ) {
      let errorMsg = 'A SSN or a Driver\'s License Number is required for Covid-19 HRSA claims.';
      validatedForm.ssn.valid = false;
      validatedForm.ssn.message = errorMsg;

      validatedForm.driverLicenseNumber.valid = false;
      validatedForm.driverLicenseNumber.message = errorMsg;
    }
  }

  // If SSN is provided, make sure it is in the correct format
  //"^\d{3}-\d{2}-\d{4}$" or "^\d{9}$"
  if (
      validatedForm.ssn.value &&
      !/^\d{3}-\d{2}-\d{4}$/.test(validatedForm.ssn.value) &&
      !/^\d{9}$/.test(validatedForm.ssn.value)
  ) {
    validatedForm.ssn.valid = false;
    validatedForm.ssn.message = 'Please enter a valid SSN (###-##-####)';
  }

  return validatedForm;
};

/**
 * Tests that the email is not empty, does not contain a space, and contains '@' and '.' in appropriate positions.
 * 
 * @return true if the email format is valid, false otherwise
 */
const validateEmailFormat = (email) => {
	if (!email) {
		return false;
	}
	email = email.trim();
	var atpos = email.indexOf('@');
	var dotpos = email.lastIndexOf('.');
	if (email.indexOf(' ') > -1 || atpos < 1 || dotpos < atpos + 2 || dotpos + 2 >= email.length) {
		return false;
	}
	return true;
};

/**
 * The <PhoneInput> component requires initial values to begin with a '+' and country code.
 * For U.S. phone numbers (10 digits) prepend '+1'. For all other numbers, assume the country code
 *  is already part of the value and only prepend '+'.
 */
const formatInitialPhoneNumber = (phoneNumber, country) => {
  let output = phoneNumber?.replace(/\D/g, '');
  if (/^\d{10}$/.test(output)) {
    return `+1${output}`;
  } else if (output && country && isCountryUk(country) && output.startsWith('0')) {
    //format UK numbers starting with '0' to start with '+44' in order to get display value in <PhoneInput>.
    return output.replace(/^0/, '+44');
  } else if (output) {
    return `+${output}`;
  }

  // 'output ?? null' allows the value to be zero
  return output ?? null;
};

/**
 * Remove the '+1' from US phone numbers and just the '+' from any other number.
 * The values created by <PhoneInput> have a leading '+1' country code by default but other
 *  country codes may be typed in.
 */
const formatPhoneNumberForSave = (phoneNumber, country) => {
  // Strip everything except numbers and '+'
  let result = phoneNumber?.replace(/[^\d+]/g, '') || '';

  // Remove '+1' if the number starts with that
  if (result.slice(0, 2) === '+1') {
    result = result.slice(2);
  }
  // Just remove the '+' from any other '+xx' number (international)
  else if (result.slice(0, 1) === '+') {
    result = result.slice(1);
  }
  // For UK numbers, convert leading '+44' or '44' to '0'. since existing RRS Addresses.VoicePhone UK numbers saved with '0' instead of '+44'(international UK).
  if (result && country && isCountryUk(country) && (result.startsWith('+44') || result.startsWith('44'))) {
    return result.replace(/^44/, '0');
  }
  return result || null;
};

export {
  buildPatientInfoFormFromOrder,
  formatInitialPhoneNumber,
  formatPhoneNumberForSave,
  validateEmailFormat,
  validatePatientInfoForm,
  employerStates,
  shippingMethods,
  validStates,
  validPitlStates
};